import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CalendarDays, CheckCircle2, Clock3, Globe2, Settings2, Sparkles, Wallet } from 'lucide-react'
import { agendaLabService } from '../../services/api'
import {
  DAY_NAMES,
  asArray,
  buildMonthGrid,
  formatMinutesLabel,
  getDateKey,
  normalizeAppointment,
  parseUtcDate,
  normalizeRule,
  summarizeDay
} from './scheduleUtils'

const DEFAULT_TIMEZONE = 'America/Bogota'
const TYPE_OPTIONS = [
  { value: 'daily', label: 'Diaria', description: 'Mismo horario para todos los dias.' },
  { value: 'weekly', label: 'Horario semanal', description: 'Configuras tus bloques por dia y normalmente se usa de lunes a viernes.' },
  { value: 'sundayToSunday', label: 'Domingo a domingo', description: 'Inicia con todos los dias habilitados y luego ajustas lo que necesites.' }
]

const initialWeekdayRules = buildPresetRules([1, 2, 3, 4, 5])
const initialEverydayRules = buildPresetRules([0, 1, 2, 3, 4, 5, 6])
const initialDailyBlocks = [
  { startTime: '08:00', endTime: '12:00', isActive: true },
  { startTime: '14:00', endTime: '18:00', isActive: true }
]

function buildPresetRules(days) {
  return days.flatMap((dayOfWeek) => ([
    { dayOfWeek, startTime: '08:00', endTime: '12:00', isActive: true },
    { dayOfWeek, startTime: '14:00', endTime: '18:00', isActive: true }
  ]))
}

function buildDailyRules(blocks) {
  return Array.from({ length: 7 }, (_, dayOfWeek) => (
    blocks.map((block) => ({
      dayOfWeek,
      startTime: block.startTime,
      endTime: block.endTime,
      isActive: block.isActive
    }))
  )).flat()
}

function resolveEffectiveRules(type, currentRules, currentDailyBlocks) {
  if (type === 'daily') {
    return buildDailyRules(currentDailyBlocks)
  }

  return currentRules
}

function buildDefaultDraftState(currentType, currentRules, currentDailyBlocks) {
  const normalizedType = currentType || 'daily'

  if (normalizedType === 'daily') {
    const nextDailyBlocks = currentDailyBlocks?.length ? currentDailyBlocks : initialDailyBlocks
    return {
      scheduleType: 'daily',
      rules: buildDailyRules(nextDailyBlocks),
      dailyBlocks: nextDailyBlocks
    }
  }

  if (normalizedType === 'sundayToSunday') {
    return {
      scheduleType: 'sundayToSunday',
      rules: currentRules?.length ? currentRules : initialEverydayRules,
      dailyBlocks: currentDailyBlocks?.length ? currentDailyBlocks : initialDailyBlocks
    }
  }

  return {
    scheduleType: 'weekly',
    rules: currentRules?.length ? currentRules : initialWeekdayRules,
    dailyBlocks: currentDailyBlocks?.length ? currentDailyBlocks : initialDailyBlocks
  }
}

function extractDailyBlocks(loadedRules) {
  const mondayRules = loadedRules
    .filter((rule) => rule.dayOfWeek === 1)
    .map((rule) => ({
      startTime: rule.startTime,
      endTime: rule.endTime,
      isActive: rule.isActive
    }))

  return mondayRules.length > 0 ? mondayRules : initialDailyBlocks
}

function inferScheduleType(loadedRules) {
  if (!loadedRules.length) {
    return ''
  }

  const activeRules = loadedRules.filter((rule) => rule.isActive)
  const days = [...new Set(activeRules.map((rule) => rule.dayOfWeek))].sort((left, right) => left - right)
  const hasAllDays = days.length === 7 && days.every((day, index) => day === index)
  const mondaySignature = activeRules
    .filter((rule) => rule.dayOfWeek === 1)
    .map((rule) => `${rule.startTime}-${rule.endTime}-${rule.isActive}`)
    .sort()
    .join('|')

  const sameDailyPattern = hasAllDays && days.every((day) => (
    activeRules
      .filter((rule) => rule.dayOfWeek === day)
      .map((rule) => `${rule.startTime}-${rule.endTime}-${rule.isActive}`)
      .sort()
      .join('|') === mondaySignature
  ))

  if (sameDailyPattern) {
    return 'daily'
  }

  if (hasAllDays) {
    return 'sundayToSunday'
  }

  return 'weekly'
}

const monthLabel = (date) =>
  new Intl.DateTimeFormat('es-CO', { month: 'long', year: 'numeric' }).format(date)

const formatTimeForApi = (value) => {
  const raw = String(value || '').trim()
  if (!raw) return '00:00:00'
  if (/^\d{2}:\d{2}:\d{2}$/.test(raw)) return raw
  if (/^\d{2}:\d{2}$/.test(raw)) return `${raw}:00`
  return raw
}

const unwrapApiData = (response) => response?.data?.data ?? response?.data ?? {}
const normalizeServiceItem = (service) => ({
  id: service?.id || service?.Id || '',
  name: service?.name || service?.Name || 'Servicio',
  durationMinutes: Number(service?.durationMinutes ?? service?.DurationMinutes ?? 0),
  isActive: Boolean(service?.isActive ?? service?.IsActive ?? false)
})

export default function AgendaLabSchedule() {
  const navigate = useNavigate()
  const [timezone, setTimezone] = useState(DEFAULT_TIMEZONE)
  const [scheduleType, setScheduleType] = useState('')
  const [scheduleId, setScheduleId] = useState('')
  const [hasSavedSchedule, setHasSavedSchedule] = useState(false)
  const [isLocked, setIsLocked] = useState(false)
  const [rules, setRules] = useState(initialWeekdayRules)
  const [dailyBlocks, setDailyBlocks] = useState(initialDailyBlocks)
  const [publicSettings, setPublicSettings] = useState({
    isEnabled: true,
    publicAgendaUrl: '',
    displayName: 'AgendaLab',
    description: '',
    nequiKey: '',
    nequiKeyType: 'telefono'
  })
  const [currentMonth, setCurrentMonth] = useState(() => new Date())
  const [appointments, setAppointments] = useState([])
  const [services, setServices] = useState([])
  const [previewServiceId, setPreviewServiceId] = useState('')
  const [availabilitySlots, setAvailabilitySlots] = useState([])
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [saving, setSaving] = useState(false)
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false)
  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false)
  const [scheduleModalMode, setScheduleModalMode] = useState('edit')
  const [draftScheduleType, setDraftScheduleType] = useState('')
  const [draftRules, setDraftRules] = useState(initialWeekdayRules)
  const [draftDailyBlocks, setDraftDailyBlocks] = useState(initialDailyBlocks)

  const loadMonth = async (monthDate = currentMonth) => {
    const from = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1)
    const to = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1)
    const response = await agendaLabService.getCalendar(from.toISOString(), to.toISOString())
    setAppointments(asArray(response.data).map(normalizeAppointment))
  }

  const loadAvailabilityPreview = async (monthDate = currentMonth, serviceId = previewServiceId) => {
    if (!serviceId) {
      setAvailabilitySlots([])
      return
    }

    const from = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1)
    const to = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1)
    const response = await agendaLabService.getAvailability(
      serviceId,
      from.toISOString(),
      to.toISOString()
    )
    setAvailabilitySlots(asArray(unwrapApiData(response)))
  }

  const applyLoadedSchedule = (schedule) => {
    const loadedRules = asArray(schedule?.rules || schedule?.Rules).map(normalizeRule)
    const loadedType = schedule?.scheduleType || schedule?.ScheduleType || inferScheduleType(loadedRules)
    const loadedScheduleId = schedule?.scheduleId || schedule?.ScheduleId || schedule?.id || schedule?.Id || ''
    const explicitHasSavedSchedule = schedule?.hasSavedSchedule ?? schedule?.HasSavedSchedule
    const inferredHasSavedSchedule = Boolean(loadedScheduleId) || loadedRules.length > 0
    const loadedHasSavedSchedule = explicitHasSavedSchedule ?? inferredHasSavedSchedule

    setScheduleId(loadedScheduleId)
    setTimezone(schedule?.timezone || schedule?.Timezone || DEFAULT_TIMEZONE)
    setScheduleType(loadedType)
    setHasSavedSchedule(loadedHasSavedSchedule)
    setIsLocked(Boolean(schedule?.isLocked ?? schedule?.IsLocked ?? loadedHasSavedSchedule))

    if (loadedType === 'daily') {
      setDailyBlocks(extractDailyBlocks(loadedRules))
      setRules(buildDailyRules(extractDailyBlocks(loadedRules)))
      return
    }

    if (loadedRules.length > 0) {
      setRules(loadedRules)
    } else if (loadedType === 'sundayToSunday') {
      setRules(initialEverydayRules)
    } else {
      setRules(initialWeekdayRules)
    }
  }

  const syncDraftSchedule = (nextType, nextRules, nextDailyBlocks) => {
    setDraftScheduleType(nextType)
    setDraftRules(nextRules)
    setDraftDailyBlocks(nextDailyBlocks)
  }

  useEffect(() => {
    const load = async () => {
      try {
        setError('')
        const [scheduleResult, publicSettingsResult, servicesResult] = await Promise.allSettled([
          agendaLabService.getSchedule(),
          agendaLabService.getPublicBookingSettings(),
          agendaLabService.getServices()
        ])

        if (scheduleResult.status === 'fulfilled') {
          applyLoadedSchedule(unwrapApiData(scheduleResult.value))
        }

        if (publicSettingsResult.status === 'fulfilled') {
          const publicBookingSettings = unwrapApiData(publicSettingsResult.value)
          setPublicSettings({
            isEnabled: publicBookingSettings?.isEnabled ?? publicBookingSettings?.IsEnabled ?? true,
            publicAgendaUrl: publicBookingSettings?.publicAgendaUrl || publicBookingSettings?.PublicAgendaUrl || '',
            displayName: publicBookingSettings?.displayName || publicBookingSettings?.DisplayName || 'AgendaLab',
            description: publicBookingSettings?.description || publicBookingSettings?.Description || '',
            nequiKey: publicBookingSettings?.nequiKey || publicBookingSettings?.NequiKey || '',
            nequiKeyType: publicBookingSettings?.nequiKeyType || publicBookingSettings?.NequiKeyType || 'telefono'
          })
        }

        if (servicesResult.status === 'fulfilled') {
          const loadedServices = asArray(unwrapApiData(servicesResult.value)).map(normalizeServiceItem)
          const activeServices = loadedServices.filter((service) => service.isActive)
          setServices(loadedServices)
          setPreviewServiceId(activeServices[0]?.id || loadedServices[0]?.id || '')
        }

        if (scheduleResult.status === 'rejected' && publicSettingsResult.status === 'rejected' && servicesResult.status === 'rejected') {
          throw scheduleResult.reason || publicSettingsResult.reason || servicesResult.reason || new Error('No se pudo cargar la programacion.')
        }

        await loadMonth(currentMonth)
      } catch (err) {
        setError(err?.message || 'No se pudo cargar la programacion.')
      }
    }

    load()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const load = async () => {
      try {
        setError('')
        await loadMonth(currentMonth)
      } catch (err) {
        setError(err?.message || 'No se pudo cargar la agenda del mes.')
      }
    }

    load()
  }, [currentMonth]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const loadPreview = async () => {
      if (!previewServiceId) {
        setAvailabilitySlots([])
        return
      }

      try {
        await loadAvailabilityPreview(currentMonth, previewServiceId)
      } catch (err) {
        setAvailabilitySlots([])
        setError(err?.message || 'No se pudo cargar la disponibilidad real del servicio.')
      }
    }

    loadPreview()
  }, [currentMonth, previewServiceId])

  const effectiveRules = useMemo(() => (
    resolveEffectiveRules(scheduleType, rules, dailyBlocks)
  ), [dailyBlocks, rules, scheduleType])

  const buildSchedulePayloadRules = (type, nextRules, nextDailyBlocks) => (
    resolveEffectiveRules(type, nextRules, nextDailyBlocks).map((rule) => ({
      dayOfWeek: String(rule.dayOfWeek),
      startTime: formatTimeForApi(rule.startTime),
      endTime: formatTimeForApi(rule.endTime),
      isActive: String(Boolean(rule.isActive))
    }))
  )

  const calendarDays = useMemo(() => buildMonthGrid(currentMonth), [currentMonth])

  const previewService = useMemo(
    () => services.find((service) => service.id === previewServiceId) || null,
    [previewServiceId, services]
  )

  const availableSlotsByDay = useMemo(() => (
    availabilitySlots.reduce((acc, slot) => {
      const key = getDateKey(parseUtcDate(slot?.startAtUtc || slot?.StartAtUtc))
      if (!acc[key]) acc[key] = []
      acc[key].push(slot)
      return acc
    }, {})
  ), [availabilitySlots])

  const monthSummary = useMemo(() => {
    return calendarDays.map((day) => ({
      date: day,
      ...summarizeDay({ date: day, rules: effectiveRules, appointments }),
      slotCount: (availableSlotsByDay[getDateKey(day)] || []).length
    }))
  }, [appointments, availableSlotsByDay, calendarDays, effectiveRules])

  const scheduledDaysCount = monthSummary.filter((item) => item.hasAppointments && item.date.getMonth() === currentMonth.getMonth()).length
  const availableDaysCount = monthSummary.filter((item) => item.slotCount > 0 && item.date.getMonth() === currentMonth.getMonth()).length
  const hasActiveSchedule = Boolean(scheduleId) || hasSavedSchedule || isLocked
  const isReadOnly = hasActiveSchedule && isLocked

  const changeRule = (index, field, value) => {
    setRules((currentRules) => currentRules.map((rule, ruleIndex) => (
      ruleIndex === index ? { ...rule, [field]: value } : rule
    )))
  }

  const changeDraftRule = (index, field, value) => {
    setDraftRules((currentRules) => currentRules.map((rule, ruleIndex) => (
      ruleIndex === index ? { ...rule, [field]: value } : rule
    )))
  }

  const addRule = () => {
    setRules((currentRules) => ([...currentRules, { dayOfWeek: 1, startTime: '08:00', endTime: '12:00', isActive: true }]))
  }

  const addDraftRule = () => {
    setDraftRules((currentRules) => ([...currentRules, { dayOfWeek: 1, startTime: '08:00', endTime: '12:00', isActive: true }]))
  }

  const removeRule = (index) => {
    setRules((currentRules) => currentRules.filter((_, ruleIndex) => ruleIndex !== index))
  }

  const removeDraftRule = (index) => {
    setDraftRules((currentRules) => currentRules.filter((_, ruleIndex) => ruleIndex !== index))
  }

  const changeDailyBlock = (index, field, value) => {
    setDailyBlocks((currentBlocks) => currentBlocks.map((block, blockIndex) => (
      blockIndex === index ? { ...block, [field]: value } : block
    )))
  }

  const changeDraftDailyBlock = (index, field, value) => {
    setDraftDailyBlocks((currentBlocks) => currentBlocks.map((block, blockIndex) => (
      blockIndex === index ? { ...block, [field]: value } : block
    )))
  }

  const addDailyBlock = () => {
    setDailyBlocks((currentBlocks) => ([...currentBlocks, { startTime: '08:00', endTime: '12:00', isActive: true }]))
  }

  const addDraftDailyBlock = () => {
    setDraftDailyBlocks((currentBlocks) => ([...currentBlocks, { startTime: '08:00', endTime: '12:00', isActive: true }]))
  }

  const removeDailyBlock = (index) => {
    setDailyBlocks((currentBlocks) => currentBlocks.filter((_, blockIndex) => blockIndex !== index))
  }

  const removeDraftDailyBlock = (index) => {
    setDraftDailyBlocks((currentBlocks) => currentBlocks.filter((_, blockIndex) => blockIndex !== index))
  }

  const selectScheduleType = (value) => {
    setScheduleType(value)
    setSuccess('')
    setError('')

    if (value === 'daily') {
      setDailyBlocks(initialDailyBlocks)
      setRules(buildDailyRules(initialDailyBlocks))
      return
    }

    if (value === 'sundayToSunday') {
      setRules(initialEverydayRules)
      return
    }

    setRules(initialWeekdayRules)
  }

  const selectDraftScheduleType = (value) => {
    setDraftScheduleType(value)

    if (value === 'daily') {
      setDraftDailyBlocks(initialDailyBlocks)
      setDraftRules(buildDailyRules(initialDailyBlocks))
      return
    }

    if (value === 'sundayToSunday') {
      setDraftRules(initialEverydayRules)
      return
    }

    setDraftRules(initialWeekdayRules)
  }

  const openScheduleModal = (mode = 'edit') => {
    const draftState = buildDefaultDraftState(scheduleType, rules, dailyBlocks)
    syncDraftSchedule(draftState.scheduleType, draftState.rules, draftState.dailyBlocks)
    setScheduleModalMode(mode)
    setIsScheduleModalOpen(true)
  }

  const closeScheduleModal = () => {
    setIsScheduleModalOpen(false)
    setScheduleModalMode('edit')
  }

  const acceptScheduleModal = async () => {
    if (!draftScheduleType) {
      setError('Debes seleccionar un tipo de programacion antes de continuar.')
      return
    }

    const nextRules = draftScheduleType === 'daily' ? buildDailyRules(draftDailyBlocks) : draftRules

    setScheduleType(draftScheduleType)
    setRules(nextRules)
    setDailyBlocks(draftDailyBlocks)
    setError('')

    if (scheduleModalMode === 'regenerate') {
      try {
        setSaving(true)
        setSuccess('')
        await agendaLabService.regenerateSchedule()
        setScheduleId('')
        setHasSavedSchedule(false)
        setIsLocked(false)
        await saveCurrentConfiguration({
          scheduleType: draftScheduleType,
          rules: nextRules,
          dailyBlocks: draftDailyBlocks
        })
        setSuccess('La programacion anterior fue eliminada logicamente y se genero una nueva programacion con la configuracion seleccionada.')
        closeScheduleModal()
      } catch (err) {
        setError(err?.message || 'No se pudo regenerar la programacion.')
      } finally {
        setSaving(false)
      }

      return
    }

    if (!hasActiveSchedule) {
      try {
        setSaving(true)
        setSuccess('')
        await saveCurrentConfiguration({
          scheduleType: draftScheduleType,
          rules: nextRules,
          dailyBlocks: draftDailyBlocks
        })
        setSuccess('Programacion guardada correctamente. Ahora si deseas cambiarla, usa el boton Regenerar.')
        closeScheduleModal()
      } catch (err) {
        setError(err?.message || 'No se pudo guardar la programacion.')
      } finally {
        setSaving(false)
      }

      return
    }

    setSuccess('Usa Regenerar horario si necesitas reemplazar la programacion actual.')
    closeScheduleModal()
  }

  const scheduleSummary = useMemo(() => {
    const option = TYPE_OPTIONS.find((item) => item.value === scheduleType)
    if (!option) {
      return 'No has seleccionado un horario todavia.'
    }

    if (scheduleType === 'daily') {
      return `${option.label}: ${dailyBlocks.length} bloque(s) diario(s).`
    }

    return `${option.label}: ${rules.length} bloque(s) configurado(s).`
  }, [dailyBlocks.length, rules.length, scheduleType])

  const scheduleTypeLabel = TYPE_OPTIONS.find((item) => item.value === scheduleType)?.label || 'Sin definir'

  const syncSavedPublicSettings = (savedPublicPayload) => {
    setPublicSettings((current) => ({
      ...current,
      isEnabled: savedPublicPayload?.isEnabled ?? savedPublicPayload?.IsEnabled ?? current.isEnabled,
      publicAgendaUrl: savedPublicPayload?.publicAgendaUrl || savedPublicPayload?.PublicAgendaUrl || current.publicAgendaUrl,
      displayName: savedPublicPayload?.displayName || savedPublicPayload?.DisplayName || current.displayName,
      description: savedPublicPayload?.description ?? savedPublicPayload?.Description ?? current.description,
      nequiKey: savedPublicPayload?.nequiKey ?? savedPublicPayload?.NequiKey ?? current.nequiKey,
      nequiKeyType: savedPublicPayload?.nequiKeyType || savedPublicPayload?.NequiKeyType || current.nequiKeyType
    }))
  }

  const saveCurrentConfiguration = async (configuration = {}) => {
    const nextTimezone = configuration.timezone ?? timezone
    const nextScheduleType = configuration.scheduleType ?? scheduleType
    const nextRules = configuration.rules ?? rules
    const nextDailyBlocks = configuration.dailyBlocks ?? dailyBlocks

    const [savedSchedule, savedPublicSettings] = await Promise.all([
      agendaLabService.saveSchedule({
        timezone: nextTimezone,
        scheduleType: nextScheduleType,
        rules: buildSchedulePayloadRules(nextScheduleType, nextRules, nextDailyBlocks)
      }),
      agendaLabService.savePublicBookingSettings({
        isEnabled: publicSettings.isEnabled,
        displayName: publicSettings.displayName,
        description: publicSettings.description,
        nequiKey: publicSettings.nequiKey,
        nequiKeyType: publicSettings.nequiKeyType
      })
    ])

    const savedSchedulePayload = unwrapApiData(savedSchedule)
    if (savedSchedulePayload && Object.keys(savedSchedulePayload).length > 0) {
      applyLoadedSchedule(savedSchedulePayload)
    }

    syncSavedPublicSettings(unwrapApiData(savedPublicSettings))

    const refreshedSchedule = unwrapApiData(await agendaLabService.getSchedule())
    if (refreshedSchedule && Object.keys(refreshedSchedule).length > 0) {
      applyLoadedSchedule(refreshedSchedule)
    }

    if (previewServiceId) {
      await loadAvailabilityPreview(currentMonth, previewServiceId)
    }
  }

  const previousMonth = () => {
    setCurrentMonth((month) => new Date(month.getFullYear(), month.getMonth() - 1, 1))
  }

  const nextMonth = () => {
    setCurrentMonth((month) => new Date(month.getFullYear(), month.getMonth() + 1, 1))
  }

  const openDay = (date) => {
    navigate(`/agendalab/schedule/day/${getDateKey(date)}`)
  }

  return (
    <div className="space-y-5 sm:space-y-6">
      <div className="relative overflow-hidden rounded-[28px] border border-slate-200 bg-gradient-to-br from-[#f8f5ee] via-white to-[#eef6f2] p-4 shadow-[0_24px_60px_rgba(15,23,42,0.08)] sm:rounded-[30px] sm:p-6">
        <div className="absolute right-0 top-0 h-36 w-36 rounded-full bg-emerald-100/70 blur-3xl" />
        <div className="absolute left-10 top-10 h-24 w-24 rounded-full bg-amber-100/60 blur-2xl" />

        <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/85 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-slate-600">
              <Sparkles className="h-3.5 w-3.5" />
              AgendaLab
            </div>
            <h1 className="mt-4 text-2xl font-semibold tracking-tight text-slate-900 sm:text-4xl">Programacion y reserva publica</h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600 sm:text-base">
              Organiza la disponibilidad del emprendedor, define el pago manual y deja lista la experiencia publica en un solo flujo claro.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-white/85 px-4 py-4">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                <Clock3 className="h-4 w-4" />
                Horario
              </div>
              <p className="mt-3 text-sm font-semibold text-slate-900">{scheduleTypeLabel}</p>
              <p className="mt-1 text-xs text-slate-500">{scheduleSummary}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white/85 px-4 py-4">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                <Globe2 className="h-4 w-4" />
                Publica
              </div>
              <p className="mt-3 text-sm font-semibold text-slate-900">{publicSettings.isEnabled ? 'Activa' : 'Pausada'}</p>
              <p className="mt-1 text-xs text-slate-500">Controla la visibilidad de la agenda publica.</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white/85 px-4 py-4">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                <CheckCircle2 className="h-4 w-4" />
                Estado
              </div>
              <p className="mt-3 text-sm font-semibold text-slate-900">{hasActiveSchedule ? 'Programacion guardada' : 'Pendiente por guardar'}</p>
              <p className="mt-1 text-xs text-slate-500">{hasActiveSchedule ? 'Puedes regenerar si necesitas rehacerla.' : 'Configura el horario en el popup para guardarlo.'}</p>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="card p-3 text-sm text-red-700 bg-red-50 border border-red-200">{error}</div>}
      {success && <div className="card p-3 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200">{success}</div>}

      <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_24px_60px_rgba(15,23,42,0.08)] sm:rounded-[30px]">
        <div className="border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white px-4 py-4 sm:px-6 sm:py-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Reserva publica y pago manual</h2>
              <p className="mt-1 text-sm text-slate-500">
                Completa la informacion visible para el usuario y deja listas las acciones clave del formulario.
              </p>
            </div>
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
              <CheckCircle2 className="h-4 w-4" />
              Flujo unificado de configuracion
            </div>
          </div>
        </div>

        <div className="space-y-4 p-4 sm:space-y-5 sm:p-6">
        <div className="grid gap-4 md:grid-cols-2">
          {hasActiveSchedule && (
            <div className="md:col-span-2 rounded-3xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white p-5 text-sm text-emerald-800">
              <div className="flex items-start gap-3">
                <div className="rounded-2xl bg-emerald-100 p-2 text-emerald-700">
                  <CheckCircle2 className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-semibold">El usuario ya tiene guardada una programacion</div>
                  <div className="mt-1">Id unico de la programacion: <span className="font-mono">{scheduleId}</span></div>
                  <div className="mt-2">
                    Si deseas regenerar, se va a reprogramar de nuevo mediante un borrado logico. Si cancelas, se mantiene la programacion seleccionada.
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="rounded-3xl border border-slate-200 bg-slate-50/80 p-4">
            <div className="flex items-start gap-3">
              <div className="rounded-2xl bg-slate-900 p-2 text-white">
                <Settings2 className="h-4 w-4" />
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-medium text-slate-800">Configuracion de horario</label>
                <p className="text-sm text-slate-600">{scheduleSummary}</p>
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-[#faf7f1] p-4">
            <label className="flex items-center gap-3 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={publicSettings.isEnabled}
                onChange={(e) => setPublicSettings((current) => ({ ...current, isEnabled: e.target.checked }))}
              />
              <span>
                <span className="block font-medium text-slate-900">Habilitar agenda publica</span>
                <span className="mt-1 block text-xs text-slate-500">Activa o pausa las reservas visibles para el usuario final.</span>
              </span>
            </label>
          </div>

          <div className="md:col-span-2 rounded-3xl border border-slate-200 bg-gradient-to-r from-slate-50 to-white p-4">
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <div className="rounded-2xl bg-amber-100 p-2 text-amber-700">
                  <Wallet className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">Acciones del formulario</p>
                  <p className="mt-1 text-sm text-slate-500">Abre los popups necesarios y guarda la configuracion cuando todo este listo.</p>
                </div>
              </div>

              <div className={`grid gap-3 ${hasActiveSchedule ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
                <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                  <button
                    className="inline-flex h-12 w-full items-center justify-center rounded-2xl border border-slate-300 bg-white text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                    type="button"
                    onClick={() => openScheduleModal('edit')}
                    title="Abrir configuracion de horario"
                    aria-label="Abrir configuracion de horario"
                  >
                    <Settings2 className="h-5 w-5" />
                  </button>
                  <p className="mt-3 text-center text-sm font-medium text-slate-900">Horario</p>
                  <p className="mt-1 text-center text-xs text-slate-500">Abre el popup para ajustar bloques y tipo de programacion.</p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                  <button
                    className="inline-flex h-12 w-full items-center justify-center rounded-2xl border border-slate-300 bg-white text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
                    type="button"
                    onClick={() => setIsCalendarModalOpen(true)}
                    title="Ver calendario en popup"
                    aria-label="Ver calendario en popup"
                  >
                    <CalendarDays className="h-5 w-5" />
                  </button>
                  <p className="mt-3 text-center text-sm font-medium text-slate-900">Calendario</p>
                  <p className="mt-1 text-center text-xs text-slate-500">Consulta la vista mensual en popup sin recargar la pagina.</p>
                </div>

                {hasActiveSchedule && (
                  <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                    <button
                      className="inline-flex h-12 w-full items-center justify-center rounded-2xl border border-slate-300 bg-white text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                      type="button"
                      onClick={() => openScheduleModal('regenerate')}
                      disabled={saving}
                    >
                      <Sparkles className="h-5 w-5" />
                    </button>
                    <p className="mt-3 text-center text-sm font-medium text-slate-900">Regenerar horario</p>
                    <p className="mt-1 text-center text-xs text-slate-500">Rehace la programacion actual sin mezclarlo con el guardado de configuracion.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        </div>
      </section>

      {isScheduleModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/55 px-4 py-6">
          <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-[28px] bg-white p-4 shadow-2xl sm:p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {scheduleModalMode === 'regenerate' ? 'Regenerar programacion' : 'Configuracion de horario'}
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  {scheduleModalMode === 'regenerate'
                    ? 'Primero elige el nuevo tipo de programacion, ajusta horarios y franjas, y luego confirma la regeneracion.'
                    : 'Ajusta el horario en este popup. Al pulsar Aceptar se guardara la programacion si aun no existe una activa.'}
                </p>
              </div>
              <button className="btn btn-secondary w-full sm:w-auto" type="button" onClick={closeScheduleModal}>
                Cerrar
              </button>
            </div>

            <div className="mt-6 space-y-5">
              {scheduleModalMode === 'regenerate' && hasActiveSchedule && (
                <div className="rounded-[24px] border border-amber-200 bg-gradient-to-r from-amber-50 to-white p-5">
                  <div className="flex items-start gap-3">
                    <div className="rounded-2xl bg-amber-100 p-2 text-amber-700">
                      <Sparkles className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Ya existe una programacion activa</p>
                      <p className="mt-1 text-sm text-slate-600">
                        Si confirmas, se hara un borrado logico de la programacion actual y se creara una nueva con las franjas que elijas aqui.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Tipo de programacion</label>
                  <p className="mt-1 text-sm text-slate-500">Escoge primero como quieres organizar la agenda y despues configura las franjas.</p>
                </div>

                <div className="grid gap-3 md:grid-cols-3">
                  {TYPE_OPTIONS.map((option) => {
                    const isSelected = draftScheduleType === option.value
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => selectDraftScheduleType(option.value)}
                        className={`rounded-[24px] border p-4 text-left transition ${isSelected
                          ? 'border-slate-900 bg-slate-900 text-white shadow-lg'
                          : 'border-slate-200 bg-slate-50 text-slate-900 hover:border-slate-300 hover:bg-white'}`}
                      >
                        <div className="text-sm font-semibold">{option.label}</div>
                        <p className={`mt-2 text-sm leading-6 ${isSelected ? 'text-slate-200' : 'text-slate-500'}`}>{option.description}</p>
                      </button>
                    )
                  })}
                </div>
              </div>

              {!draftScheduleType && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                  Primero elige si tu programacion sera diaria, horario semanal o domingo a domingo.
                </div>
              )}

              {draftScheduleType === 'daily' && (
                <div className="space-y-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Bloques diarios</h3>
                      <p className="text-sm text-gray-500">Estos bloques se repetiran todos los dias de la semana.</p>
                    </div>
                    <button className="btn btn-secondary w-full sm:w-auto" type="button" onClick={addDraftDailyBlock}>
                      Agregar bloque
                    </button>
                  </div>

                  <div className="space-y-3">
                    {draftDailyBlocks.map((block, index) => (
                      <div key={`draft-daily-${index}`} className="grid gap-3 rounded-2xl border border-gray-200 bg-gray-50 p-4 md:grid-cols-[1fr_1fr_auto_auto] md:items-end">
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">Desde</label>
                          <input
                            className="input"
                            type="time"
                            value={block.startTime}
                            onChange={(e) => changeDraftDailyBlock(index, 'startTime', e.target.value)}
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">Hasta</label>
                          <input
                            className="input"
                            type="time"
                            value={block.endTime}
                            onChange={(e) => changeDraftDailyBlock(index, 'endTime', e.target.value)}
                          />
                        </div>

                        <label className="flex items-center gap-2 text-sm text-gray-700 md:pb-2">
                          <input
                            type="checkbox"
                            checked={block.isActive}
                            onChange={(e) => changeDraftDailyBlock(index, 'isActive', e.target.checked)}
                          />
                          Activo
                        </label>

                        <button className="text-left text-sm text-red-600 hover:text-red-700 md:pb-2" type="button" onClick={() => removeDraftDailyBlock(index)}>
                          Quitar
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {draftScheduleType && draftScheduleType !== 'daily' && (
                <div className="space-y-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {draftScheduleType === 'sundayToSunday' ? 'Horario domingo a domingo' : 'Horario semanal'}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {draftScheduleType === 'sundayToSunday'
                          ? 'Partes con todos los dias activos y luego ajustas cada bloque.'
                          : 'Crea los bloques de atencion que tendra tu agenda durante la semana.'}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                      {draftScheduleType === 'weekly' && (
                        <button className="btn btn-secondary w-full sm:w-auto" type="button" onClick={() => setDraftRules(initialWeekdayRules)}>
                          Lunes a viernes
                        </button>
                      )}
                      {draftScheduleType === 'sundayToSunday' && (
                        <button className="btn btn-secondary w-full sm:w-auto" type="button" onClick={() => setDraftRules(initialEverydayRules)}>
                          Domingo a domingo
                        </button>
                      )}
                      <button className="btn btn-secondary w-full sm:w-auto" type="button" onClick={addDraftRule}>
                        Agregar bloque
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {draftRules.map((rule, index) => (
                      <div key={`draft-${rule.dayOfWeek}-${index}`} className="grid gap-3 rounded-2xl border border-gray-200 bg-gray-50 p-4 md:grid-cols-[1.1fr_1fr_1fr_auto_auto] md:items-end">
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">Dia</label>
                          <select
                            className="input"
                            value={rule.dayOfWeek}
                            onChange={(e) => changeDraftRule(index, 'dayOfWeek', Number(e.target.value))}
                          >
                            {DAY_NAMES.map((dayName, dayIndex) => (
                              <option key={dayName} value={dayIndex}>{dayName}</option>
                            ))}
                          </select>
                        </div>

                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">Desde</label>
                          <input
                            className="input"
                            type="time"
                            value={rule.startTime}
                            onChange={(e) => changeDraftRule(index, 'startTime', e.target.value)}
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">Hasta</label>
                          <input
                            className="input"
                            type="time"
                            value={rule.endTime}
                            onChange={(e) => changeDraftRule(index, 'endTime', e.target.value)}
                          />
                        </div>

                        <label className="flex items-center gap-2 text-sm text-gray-700 md:pb-2">
                          <input type="checkbox" checked={rule.isActive} onChange={(e) => changeDraftRule(index, 'isActive', e.target.checked)} />
                          Activo
                        </label>

                        <button className="text-left text-sm text-red-600 hover:text-red-700 md:pb-2" type="button" onClick={() => removeDraftRule(index)}>
                          Quitar
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button className="btn btn-secondary w-full sm:w-auto" type="button" onClick={closeScheduleModal}>
                Cancelar
              </button>
              <button className="btn btn-primary w-full sm:w-auto" type="button" onClick={acceptScheduleModal} disabled={saving}>
                {saving ? 'Procesando...' : scheduleModalMode === 'regenerate' ? 'Confirmar regeneracion' : 'Aceptar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isCalendarModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/55 px-4 py-6">
          <div className="max-h-[90vh] w-full max-w-6xl overflow-y-auto rounded-[28px] bg-white p-4 shadow-2xl sm:p-6">
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Calendario</h2>
                <p className="mt-1 text-sm text-gray-500">Vista mensual de la agenda en popup.</p>
              </div>
              <button className="btn btn-secondary w-full sm:w-auto" type="button" onClick={() => setIsCalendarModalOpen(false)}>
                Cerrar
              </button>
            </div>

            <section className="card p-5 space-y-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Vista mensual de la agenda</h2>
                  <p className="text-sm text-gray-500">Rojo tenue: hay tiempo disponible. Verde: ya tienes citas programadas.</p>
                </div>

                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <button className="btn btn-secondary w-full sm:w-auto" type="button" onClick={previousMonth}>Mes anterior</button>
                  <div className="min-w-[180px] text-center text-sm font-semibold text-gray-700 capitalize">{monthLabel(currentMonth)}</div>
                  <button className="btn btn-secondary w-full sm:w-auto" type="button" onClick={nextMonth}>Mes siguiente</button>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4">
                  <p className="text-sm text-rose-700">Dias con espacios reservables</p>
                  <p className="mt-2 text-2xl font-bold text-rose-900">{availableDaysCount}</p>
                </div>
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                  <p className="text-sm text-emerald-700">Dias con citas programadas</p>
                  <p className="mt-2 text-2xl font-bold text-emerald-900">{scheduledDaysCount}</p>
                </div>
                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                  <p className="text-sm text-gray-600">Servicio de referencia</p>
                  <p className="mt-2 text-sm text-gray-700">{previewService ? `${previewService.name} · ${previewService.durationMinutes} min` : 'Activa al menos un servicio para ver disponibilidad real.'}</p>
                </div>
              </div>

              <div className="overflow-x-auto pb-2">
                <div className="min-w-[640px]">
                  <div className="grid grid-cols-7 gap-2 text-center text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {DAY_NAMES.map((dayName) => (
                      <div key={`modal-${dayName}`} className="py-2">{dayName.slice(0, 3)}</div>
                    ))}
                  </div>

                  <div className="grid grid-cols-7 gap-2">
                    {monthSummary.map((dayInfo) => {
                      const isCurrentMonth = dayInfo.date.getMonth() === currentMonth.getMonth()
                      const hasReservableSlots = dayInfo.slotCount > 0
                      const classes = dayInfo.hasAppointments && hasReservableSlots
                        ? 'border-emerald-200 bg-gradient-to-br from-rose-50 to-emerald-50'
                        : dayInfo.hasAppointments
                          ? 'border-emerald-200 bg-emerald-50'
                          : hasReservableSlots
                            ? 'border-rose-200 bg-rose-50'
                            : 'border-gray-200 bg-white'

                      return (
                        <button
                          key={`modal-${getDateKey(dayInfo.date)}`}
                          type="button"
                          onClick={() => openDay(dayInfo.date)}
                          className={`min-h-[120px] rounded-2xl border p-3 text-left transition hover:-translate-y-0.5 hover:shadow-sm ${classes} ${isCurrentMonth ? 'opacity-100' : 'opacity-45'}`}
                        >
                          <div className="flex items-start justify-between">
                            <span className="text-sm font-semibold text-gray-900">{dayInfo.date.getDate()}</span>
                            {dayInfo.hasAppointments && (
                              <span className="rounded-full bg-emerald-100 px-2 py-1 text-[11px] font-medium text-emerald-700">
                                {dayInfo.scheduledCount} cita(s)
                              </span>
                            )}
                          </div>

                          <div className="mt-3 space-y-2 text-xs">
                            <div className={`${hasReservableSlots ? 'text-rose-700' : 'text-gray-400'}`}>
                              Disponible: {hasReservableSlots ? `${dayInfo.slotCount} espacio(s)` : 'Sin espacios'}
                            </div>
                            <div className={`${dayInfo.hasAppointments ? 'text-emerald-700' : 'text-gray-400'}`}>
                              Programado: {dayInfo.hasAppointments ? formatMinutesLabel(dayInfo.scheduledMinutes) : 'Sin citas'}
                            </div>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      )}
    </div>
  )
}
